import { StatTile } from '@/components/common/StatTile';
import { ChartCard } from '@/components/charts/ChartCard';
import { LineChart } from '@/components/charts/LineChart';
import { DonutChart } from '@/components/charts/DonutChart';
import { semanticSeries } from '@/components/charts/chartTheme';
import { WidgetCard } from '@/components/common/WidgetCard';
import { QueueStateChip } from '@/features/queue/components/QueueChips';
import { formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { QueueOverview as QueueOverviewData, QueueBucket } from '@/features/queue/types';
import {
  BoltIcon, ClockIcon, ArrowPathIcon, ExclamationTriangleIcon, QueueListIcon,
  ServerStackIcon, CpuChipIcon,
} from '@/icons';

/** Semantic bucket colours (worse = warmer). */
const BUCKET_COLOR: Record<string, string> = {
  active: semanticSeries.info, delivery: semanticSeries.good, deferred: semanticSeries.degraded,
  retry: semanticSeries.degraded, quarantine: semanticSeries.quarantine,
  failed: semanticSeries.bad, dead: semanticSeries.terminal,
};

/**
 * Delivery Center dashboard: 8 KPI tiles + throughput trend + queue-bucket
 * distribution. Reuses StatTile, ChartCard/LineChart/DonutChart and WidgetCard.
 * Clicking a bucket in the side panel selects it in the table.
 */
export function QueueOverview({ data, activeBucket, onBucket }: {
  data: QueueOverviewData; activeBucket: string; onBucket: (key: string) => void;
}) {
  const k = data.kpis;
  const donut = data.buckets.map((b) => ({ name: b.label, value: b.count, color: BUCKET_COLOR[b.key] }));
  const avgSeconds = (k.avgDeliveryMs / 1000).toFixed(1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <StatTile label="Processing" value={formatCount(k.processing)} icon={BoltIcon} accent="brand" />
        <StatTile label="Deferred" value={formatCount(k.deferred)} icon={ClockIcon} accent="warning" />
        <StatTile label="Retried" value={formatCount(k.retried)} icon={ArrowPathIcon} accent="info" />
        <StatTile label="Failed" value={formatCount(k.failed)} icon={ExclamationTriangleIcon} accent="danger" />
        <StatTile label="Avg delivery" value={`${avgSeconds}s`} icon={ClockIcon} accent="brand" />
        <StatTile label="Queue length" value={formatCount(k.queueLength)} icon={QueueListIcon} accent="warning" />
        <StatTile label="SMTP conns" value={formatCount(k.smtpInbound + k.smtpOutbound)} icon={ServerStackIcon} accent="info" sublabel={`in ${k.smtpInbound} · out ${k.smtpOutbound}`} />
        <StatTile label="Workers" value={`${k.workersActive}/${k.workersTotal}`} icon={CpuChipIcon} accent={k.workersActive < k.workersTotal ? 'warning' : 'success'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard title="Queue throughput" description={`Delivered vs deferred · last ${data.trend.length} intervals`}>
            <LineChart data={data.trend} xKey="t" height={240}
              series={[{ key: 'delivered', name: 'Delivered', color: semanticSeries.good }, { key: 'deferred', name: 'Deferred', color: semanticSeries.degraded }]} />
          </ChartCard>
        </div>
        <ChartCard title="Queue distribution" description="Messages by logical queue">
          <DonutChart data={donut} height={240} />
        </ChartCard>
      </div>

      <WidgetCard title="Queues">
        <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {data.buckets.map((b) => <BucketRow key={b.key} bucket={b} active={b.key === activeBucket} onClick={() => onBucket(b.key)} />)}
        </ul>
      </WidgetCard>
    </div>
  );
}

function BucketRow({ bucket, active, onClick }: { bucket: QueueBucket; active: boolean; onClick: () => void }) {
  return (
    <li>
      <button type="button" onClick={onClick}
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
          active ? 'border-brand bg-brand/5' : 'border-border hover:bg-surface-raised/50',
        )}>
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: BUCKET_COLOR[bucket.key] ?? semanticSeries.terminal }} aria-hidden="true" />
          <span className="font-medium text-fg">{bucket.label}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="font-mono text-xs text-fg-muted">{formatCount(bucket.count)}</span>
          <QueueStateChip value={bucket.state} />
        </span>
      </button>
    </li>
  );
}
